<template>
  <vx-card class="create-container-wizard">
    <form-wizard
      color="rgba(var(--vs-primary), 1)"
      title=""
      subtitle=""
      @on-complete="formSubmitted"
      finishButtonText="Crear Contenedor"
      backButtonText="Atrás"
      nextButtonText="Siguiente"
    >
      <template slot="title"></template>
      <!-- Step 1: Configuración Básica -->
      <tab-content title="Configuración" icon="feather icon-settings" :before-change="validateStep1">
        <div class="vx-row">
          <!-- Section 1: Top Row (Name & Reminders) -->
          <div class="vx-col w-full mb-6 mt-4">
            <div class="vx-row">
              <div class="vx-col md:w-1/2 w-full mb-4">
                <vs-input 
                  label="Nombre del Contenedor" 
                  v-model="name" 
                  class="w-full" 
                  placeholder="Ej. Contratos Q1 2025"
                  @blur="$v.name.$touch()"
                  :danger="$v.name.$error"
                  danger-text="El nombre es obligatorio"
                />
              </div>
              <div class="vx-col md:w-1/2 w-full mb-4">
                <h5 class="mb-2 font-bold">Recordatorios</h5>
                <p class="mb-2">
                  Se enviarán {{ tries }} recordatorios cada {{ remember_every }} días a las {{ remember_at }} hrs.
                  <a href="javascript:void(0)" class="ml-2 font-bold" @click="rememberPopupEnable = true">Configurar</a>
                </p>
              </div>
            </div>
          </div>

          <vs-divider />

          <!-- Section 2: Bottom Row (Signing Type & Custom PDF) -->
          <div class="vx-col w-full mb-6">
            <div class="vx-row">
              <!-- Sign Mode Summary -->
              <div class="vx-col md:w-1/2 w-full mb-4">
                <h5 class="mb-2 font-bold">Tipo de representación de firmado</h5>
                <span>Selecciona una de las dos tipos de representaciones que ponemos a tu disposición (PAdES por defecto). 
                  <a href="javascript:void(0)" class="ml-2 font-bold" @click="signedMethodPopupEnable = true">Seleccionar</a>
                </span> <br>
                <div class="mt-2 text-sm">
                  <span>Tipo de firmado: <strong>{{ SIGN_STRATEGIES[sign_mode] }}</strong></span> <br>
                  <span v-if="sign_mode === 'PDF_SIGNATURE'">Posición seleccionada: <strong>{{ SIGN_POSITION[sign_position] }}</strong></span>
                </div>
              </div>

              <!-- Logo Mode -->
              <div class="vx-col md:w-1/2 w-full mb-4">
                <h5 class="mb-2 font-bold">PDF Personalizado</h5>
                <span>Personaliza tu PDF con el logo de tu marca o empresa. 
                  <a href="javascript:void(0)" class="ml-2 font-bold" @click="customLogoPopupEnable = true">Personalizar</a>
                </span> <br>
                <div class="mt-2 text-sm">
                  <span>Opción seleccionada: <strong>{{ LOGO_MODES[mode_logo] }}</strong></span>
                </div>
              </div>
            </div>

            <!-- Logo Selection Row (Visible if MEDIUM_BRANDING) -->
            <div class="vx-row mt-4" v-if="mode_logo === 'MEDIUM_BRANDING'">
              <div class="vx-col w-full">
                <vs-divider />
                <h5 class="mb-4 font-bold">Logo del Contenedor</h5>
                <div class="flex items-center">
                  <div class="mr-4" v-if="croppedImg">
                    <img :src="urlCroppedImg" alt="Logo recortado" class="border rounded p-1" style="max-height: 80px;">
                  </div>
                  <div v-else class="mr-4 p-4 border-dashed border-2 rounded text-grey text-center" style="width: 150px;">
                    <feather-icon icon="ImageIcon" svgClasses="w-8 h-8 mb-2" />
                    <p class="text-xs">Sin logo</p>
                  </div>
                  <div>
                    <vs-button type="border" @click="$refs.fileInputLogo.click()">
                      {{ croppedImg ? 'Cambiar Logo' : 'Seleccionar Logo' }}
                    </vs-button>
                    <input type="file" ref="fileInputLogo" class="hidden" accept="image/*" @change="processLogo">
                    <p class="text-xs mt-2 text-grey">Se recomienda un logo con fondo transparente (PNG).</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </tab-content>

      <!-- Step 2: Agregar Firmantes -->
      <tab-content title="Firmantes" icon="feather icon-users" :before-change="validateStep2">
        <div class="vx-row mt-4 px-5">
          <!-- Signers Section -->
          <div class="vx-col md:w-1/2 w-full mb-10 md:mb-0">
            <h3 class="mb-3">Añade firmantes</h3>
            <p>Ingresa el nombre y correo de las personas que firmarán el contenedor.</p>
            
            <!-- Interruptor para activar firma ordenada -->
            <div class="flex items-center mb-6 mt-4">
              <span class="mr-3">Activar firma ordenada</span>
              <vs-switch v-model="sortableSignatures"/>
            </div>

            <!-- Draggable Area for Signers -->
            <draggable v-model="signatures" :disabled="!sortableSignatures" handle=".drag-handle" ghost-class="dragging">
              <transition-group>
                <SignerCard
                  v-for="(signer, index) in signatures"
                  :key="signer.id"
                  :signer="signer"
                  :index="index"
                  :sortable="sortableSignatures"
                  @remove-signer="removeSigner"
                  @search-signers="searchSigners"
                  :email-suggestions="emailSuggestions"
                />
              </transition-group>
            </draggable>

            <vs-button class="mt-4" type="flat" @click="addNewSigner">
              <feather-icon icon="PlusIcon" svgClasses="w-4 h-4" /> Añadir otro firmante
            </vs-button>
          </div>

          <!-- Spectators Section -->
          <div class="vx-col md:w-1/2 w-full">
            <div class="flex items-center justify-between mb-4">
              <h3 class="">Espectadores</h3>
              <vs-button type="border" icon-pack="feather" icon="icon-plus" @click="spectatorPopupEnable = true">Añadir Espectador</vs-button>
            </div>

            <div v-if="!spectators.length" class="text-center p-8 bg-grey-light rounded-lg">
                <p class="text-grey">No hay espectadores agregados</p>
            </div>
            
            <vs-list v-else>
              <vs-list-item 
                v-for="(listItem, index) in spectators" 
                :key="index" 
                :title="listItem.name" 
                :subtitle="listItem.email"
              >
                <vs-avatar slot="avatar" :text="listItem.name.split(' ').slice(0,2).join(' ').trim()" />
                <vs-button
                  type="flat"
                  color="danger"
                  icon-pack="feather"
                  icon="icon-trash"
                  @click="removeSpectator(index)"
                ></vs-button>
              </vs-list-item>
            </vs-list>
          </div>
        </div>
      </tab-content>

      <!-- Step 3: Revisión -->
      <tab-content title="Revisión" icon="feather icon-check-circle" class="mb-12">
        <div class="vx-row mt-4 px-5">
          
          <div class="vx-col md:w-1/2 w-full">
            <h3 class="mb-2">Importante:</h3>
            <p class="mb-3">Revisa la configuración del contenedor y los firmantes antes de finalizar. Una vez creado, el contenedor servirá como plantilla para tus procesos de firma masiva.</p>

            <h3 class="mb-2">Recordatorios:</h3>
            <span>Se enviarán {{ tries }} recordatorios de firmado cada {{ remember_every }} dias, a las {{ remember_at }} hrs.</span>

            <h3 class="mt-5 mb-2">Configuración de Firma:</h3>
            <span>Modo de firmado: <strong>{{ SIGN_STRATEGIES[sign_mode] }}</strong></span> <br>
            <span>Posición de firma: <strong>{{ SIGN_POSITION[sign_position] }}</strong></span> <br>
            <span>Firma ordenada: <strong>{{ sortableSignatures ? 'Activada' : 'Desactivada' }}</strong></span>
          </div>

          <div class="vx-col md:w-1/2 w-full">
            <h3 class="mb-3">Detalles del contenedor</h3>
            <p class="text-bold">Nombre: {{ name }}</p>

            <vs-list class="mt-4">
              <vs-list-header title="Firmantes" color="primary"></vs-list-header>
              <vs-list-item 
                v-for="(sig, i) in signatures" 
                :key="'sig-'+i" 
                :title="sig.name || sig.email" 
                :subtitle="sig.email"
              >
                <vs-avatar slot="avatar" :text="(sig.name || 'F').split(' ').slice(0,2).join(' ').trim()" />
              </vs-list-item>
            </vs-list>

            <vs-list class="mt-4" v-if="spectators.length">
              <vs-list-header title="Espectadores" color="primary"></vs-list-header>
              <vs-list-item 
                v-for="(spec, i) in spectators" 
                :key="'spec-'+i" 
                :title="spec.name" 
                :subtitle="spec.email"
              >
                <vs-avatar slot="avatar" :text="spec.name.split(' ').slice(0,2).join(' ').trim()" />
              </vs-list-item>
            </vs-list>
          </div>

        </div>
      </tab-content>

    </form-wizard>

    <!-- Spectator Popup -->
    <vs-popup title="Añadir Espectador" :active.sync="spectatorPopupEnable">
      <div class="pt-3 px-3 pb-5">
        <h4 class="mb-4">Nuevo Espectador</h4>
        <vs-input
          class="w-full mb-4"
          label-placeholder="Nombre completo"
          v-model.trim="spectatorsForm.name"
        ></vs-input>
        <vs-input
          class="w-full mb-4"
          label-placeholder="Correo electrónico"
          v-model.trim="spectatorsForm.email"
        ></vs-input>
        <vs-button class="mt-4 w-full" @click="addSpectator">
          Añadir a la lista
        </vs-button>
      </div>
    </vs-popup>

    <!-- Reminder Modal -->
    <vs-popup title="Establecer Recordatorios" :active.sync="rememberPopupEnable">
      <vs-card>
        <div class="pt-3 px-3">
            <h3 class="mb-3">Recordatorios</h3>
            <p class="mb-5">Configura los recordatorios que recibirán tus invitados para firmar el documento</p>
            
            <h3 class="mb-3">Intentos</h3>
            <p class="mb-5">Establece el número total de recordatorios que deseas enviar.</p>
            <vs-input-number min="1" max="10" v-model="tries" />
            
            <h3 class="mb-3 mt-8">Intervalos</h3>
            <p class="mb-5">Seleciona un intervalo de días para hacerlo. (Cada día, cada 2 días, etc.).</p>
            <vs-input-number min="1" max="30" v-model="remember_every" />
            
            <h3 class="mb-3 mt-8">Hora de envío</h3>
            <p class="mb-5">Elije una hora para enviar los recordatorios</p>
            <div class="w-full md:w-1/3 ">
              <flat-pickr 
                :config="configTimePicker" 
                v-model="remember_at" 
                placeholder="HH:mm:ss AM/PM" 
                class="vs-inputx vs-input--input normal block mt-1 w-full"
              />
            </div>
        </div>
        <div slot="footer">
          <vs-row vs-justify="flex-end">
            <vs-button type="filled" color="primary" @click="rememberPopupEnable = false">Aceptar</vs-button>
          </vs-row>
        </div>
      </vs-card>
    </vs-popup>

    <!-- Sign Method Popup -->
    <vs-popup class="signed_method" title="Selecciona un Método de Firmado" :active.sync="signedMethodPopupEnable">
      <sign-selector :open-sign-position-popup="openSignPositionPopup" :submit-new-document="signSelectorAction"></sign-selector>
      <vs-popup title="Selecciona la posición de la firma" :active.sync="signPositionPopupEnable">
        <sign-position-selector :set-sign-position="setSignPosition" :submit-new-document="signSelectorAction"></sign-position-selector>
      </vs-popup>
    </vs-popup>

    <!-- Custom Logo Modal -->
    <vs-popup title="Logo Personalizado" :active.sync="customLogoPopupEnable">
      <vs-card>
        <div class="pt-3 px-3">
          <h3 class="mb-3">Hoja de firmas</h3>
          <p class="mb-5">Personaliza con el logo de tu empresa la hoja de firmas al final del documento.</p>
          <vs-row>
            <vs-col class="my-3" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
              <img src="@/assets/images/document/full-logo.svg" width="90" class="d-block m-auto" alt="">
              <vs-radio class="mt-3" v-model="mode_logo" vs-name="mode_logo" vs-value="FULL_BRANDING">Logos de Verificamex</vs-radio>
            </vs-col>

            <vs-col class="my-3" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
              <img src="@/assets/images/document/medium-logo.svg" width="90" class="d-block m-auto" alt="">
              <vs-radio class="mt-3" v-model="mode_logo" vs-name="mode_logo" vs-value="MEDIUM_BRANDING">Mi logo Personalizado</vs-radio>
            </vs-col>

            <vs-col class="my-3" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
              <img src="@/assets/images/document/upper-left.svg" width="90" class="d-block m-auto" alt="">
              <vs-radio class="mt-3" v-model="mode_logo" vs-name="mode_logo" vs-value="NO_BRANDING">Prefiero no usar logos</vs-radio>
            </vs-col>

            <vs-col class="my-3" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
              <vs-avatar size="70px" icon-pack="feather" icon="icon-slash" class="d-block m-auto" />
              <vs-radio class="mt-3" v-model="mode_logo" vs-name="mode_logo" vs-value="NO_SIGNATURE_SHEET">Sin hoja de firmas</vs-radio>
            </vs-col>
          </vs-row>
        </div>
        <div slot="footer">
          <vs-row vs-justify="flex-end">
            <vs-button type="filled" color="primary" @click="customLogoPopupEnable = false">Aceptar</vs-button>
          </vs-row>
        </div>
      </vs-card>
    </vs-popup>

    <!-- Logo Adjustment Popup -->
    <vs-popup :button-close-hidden="true" title="Ajusta tu logo" :active.sync="popupLogoAdjust">
      <cropper-logo 
        v-if="popupLogoAdjust" 
        :close-popup-logo-adjust="closePopupLogoAdjust" 
        :load-img="loadImg" 
        :data-img="dataImg" 
        :set-cropped-img="setCroppedImg" 
        :set-data-img-to-show="setDataImgToShow"
      ></cropper-logo>
    </vs-popup>
  </vx-card>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { required } from 'vuelidate/lib/validators'
import { SIGN_STRATEGIES, SIGN_POSITION } from '@/config/app'
import draggable from 'vuedraggable'
import moment from 'moment'
import SignerCard from '../../containers/containers-create/SignerCard.vue'
import SignSelector from '../../documents/components/SignSelector.vue'
import SignPositionSelector from '../../documents/components/SignPositionSelector.vue'
import CropperLogo from "@/views/pages/configuration/companies/components/CropperLogo";

export default {
  name: 'CreateContainerWizard',
  components: {
    FormWizard,
    TabContent,
    flatPickr,
    draggable,
    SignerCard,
    SignSelector,
    SignPositionSelector,
    CropperLogo
  },
  data() {
    return {
      // Step 1 Data
      name: '',
      webhook: '',
      tries: 3,
      remember_every: 3,
      remember_at: '12:00:00 PM',
      sign_mode: 'PDF_SIGNATURE',
      sign_position: 'NONE',
      mode_logo: 'FULL_BRANDING',
      notification_mode: true,

      // Logo Data
      popupLogoAdjust: false,
      dataLogo: null,
      dataImg: null,
      loadImg: null,
      croppedImg: null,
      
      // Step 2 Data
      sortableSignatures: false,
      signatures: [
        { id: Math.random().toString(36).substring(7), name: '', email: '', phone: '', rfc: '', restrictSign: false, restrictSelector: 'RFC' }
      ],
      spectators: [],
      spectatorsForm: {
        name: '',
        email: ''
      },
      spectatorPopupEnable: false,
      rememberPopupEnable: false,
      signedMethodPopupEnable: false,
      signPositionPopupEnable: false,
      customLogoPopupEnable: false,
      emailSuggestions: [],

      // Configs
      configTimePicker: {
        enableTime: true,
        enableSeconds: true,
        noCalendar: true,
        dateFormat: 'h:i:S K',
        time_24hr: false
      },
      SIGN_STRATEGIES,
      SIGN_POSITION,
      LOGO_MODES: {
        'FULL_BRANDING': 'Logos de Verificamex',
        'MEDIUM_BRANDING': 'Mi logo Personalizado',
        'NO_BRANDING': 'Prefiero no usar logos',
        'NO_SIGNATURE_SHEET': 'Sin hoja de firmas'
      },
      isFinished: false
    }
  },
  computed: {
    urlCroppedImg() {
      return this.croppedImg ? URL.createObjectURL(this.croppedImg) : null;
    }
  },
  validations: {
    name: { required }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isFinished) return next()
    
    this.$vs.dialog({
      type: 'confirm',
      color: 'danger',
      title: '¿Estás seguro?',
      text: '¡Todos los cambios se perderan!',
      acceptText: 'Si',
      cancelText: 'No',
      accept: () => {
        next()
      },
      cancel: () => {
        next(false)
      }
    })
  },
  methods: {
    validateStep1() {
      this.$v.name.$touch()
      return !this.$v.name.$invalid
    },
    validateStep2() {
        let valid = true
        this.signatures.forEach(sig => {
            if (!sig.name || !sig.email) valid = false
        })
        if (!valid) {
            this.$store.dispatch('alert/error', 'Por favor completa el nombre y email de todos los firmantes')
        }
        return valid
    },
    addNewSigner() {
        this.signatures.push({ 
            id: Math.random().toString(36).substring(7), 
            name: '', 
            email: '', 
            phone: '', 
            rfc: '',
            restrictSign: false,
            restrictSelector: 'RFC'
        })
    },
    removeSigner(index) {
        if (this.signatures.length > 1) {
            this.signatures.splice(index, 1)
        }
    },
    addSpectator() {
        if (!this.spectatorsForm.name || !this.spectatorsForm.email) {
            this.$store.dispatch('alert/error', 'Ingresa nombre y correo del espectador')
            return
        }
        this.spectators.push({ ...this.spectatorsForm })
        this.spectatorsForm = { name: '', email: '' }
        this.spectatorPopupEnable = false
    },
    removeSpectator(index) {
        this.spectators.splice(index, 1)
    },
    searchSigners(query) {
      this.$store.dispatch("document/searchSigners", {query}).then(
        response => {
          this.emailSuggestions = response.data
        }
      ).catch(() => {
      })
    },
    openSignPositionPopup() {
      this.signPositionPopupEnable = true
    },
    setSignPosition(position) {
      this.sign_position = position
    },
    signSelectorAction(selector) {
      this.sign_mode = selector
      if (selector === 'CLASSIC') {
        this.sign_position = 'NONE'
      }
      this.signPositionPopupEnable = false
      this.signedMethodPopupEnable = false
    },
    setDataImgToShow(dataImg) {
      this.dataLogo = dataImg;
    },
    setCroppedImg(croppedImg) {
      this.croppedImg = croppedImg
    },
    closePopupLogoAdjust() {
      this.popupLogoAdjust = false;
    },
    processLogo(event) {
      let file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader()
      reader.onload = e => {
        this.dataImg = file;
        this.loadImg = e.target.result;
        this.popupLogoAdjust = true;
        this.dataLogo = null;
      }
      reader.readAsDataURL(file)
    },
    formSubmitted() {
        this.$vs.loading()
        
        const payload = {
            name: this.name,
            webhook: this.webhook,
            tries: this.tries,
            remember_every: this.remember_every,
            remember_at: moment(this.remember_at, ['h:mm:ss A', 'HH:mm:ss', 'HH:mm']).format('HH:mm'),
            sign_mode: this.sign_mode,
            sign_position: this.sign_position,
            mode_logo: this.mode_logo,
            sign_ordered: this.sortableSignatures ? 'ACTIVE' : 'INACTIVE',
            notification_mode: this.notification_mode ? 'ENABLED' : 'DISABLED',
            signatures: this.signatures.map(s => {
              const sig = {
                name: s.name,
                email: s.email
              }
              if (s.phone) sig.phone = s.phone
              if (s.rfc) sig.rfc = s.rfc
              return sig
            }),
            spectators: this.spectators.map(s => ({
              name: s.name,
              email: s.email
            })),
            logo: this.croppedImg
        }

        console.log('CONTAINER PAYLOAD:', payload)

        this.$store.dispatch('containerManagement/createContainer', payload)
            .then(() => {
                this.$vs.loading.close()
                this.$vs.notify({
                    title: 'Éxito',
                    text: 'Contenedor creado correctamente',
                    color: 'success',
                    position: 'top-center',
                    time: 3000
                });
                this.isFinished = true
                this.$router.push({ 
                  name: 'create-container-success',
                  query: { autoOpen: true }
                })
            })
            .catch(error => {
                this.$vs.loading.close()
                this.$vs.notify({
                    title: 'Error',
                    text: error || 'Error al crear el contenedor',
                    color: 'danger',
                    position: 'top-center',
                    time: 3000
                });
            })
    }
  }
}
</script>

<style lang="scss" scoped>
.logo-upload-box {
  min-height: 120px;
}
.signer-row {
    background-color: #f8f8f8;
}
</style>
